import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Headphones, Save, RotateCcw, Loader2, CheckCircle2, AlertCircle, ExternalLink, Search, UploadCloud } from "lucide-react";
import AdminAuthGate from "@/components/admin/AdminAuthGate";
import { HOTELS } from "@/lib/data";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAudioGuide } from "@/contexts/AudioGuideContext";
import {
  slotForAudioGuide, DEFAULT_AUDIO_GUIDE_SRC,
} from "@/lib/useAudioGuideUrl";
import {
  ensureTextLoaded, getCachedText, persistText,
} from "@/lib/slotCache";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const ACCEPT_AUDIO = "audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/x-wav,audio/mp4,audio/aac,audio/webm,audio/opus,.mp3,.ogg,.wav,.m4a,.aac";

const pick = (obj, lang) => obj?.[lang] || obj?.en || obj?.fr || "";

const uploadAudio = async (slot, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/api/audio_files/upload?slot_id=${encodeURIComponent(slot)}`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `Upload failed (${res.status})`);
  }
  return res.json();
};

// Single row — one hotel's audio guide URL editor
const HotelAudioRow = ({ hotel, lang, initialUrl, onSaved }) => {
  const slot = slotForAudioGuide(hotel.slug);
  const [draft, setDraft] = useState(initialUrl || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const { playGuide, meta, isPlaying, stop } = useAudioGuide();

  const isUsingDefault = !draft;
  const liveSrc = (draft || "").trim() || DEFAULT_AUDIO_GUIDE_SRC;
  const isActiveHere = meta?.slug === hotel.slug && isPlaying;

  const save = async (overrideUrl) => {
    setBusy(true);
    setError(null);
    try {
      const value = (overrideUrl !== undefined ? overrideUrl : draft).trim();
      await persistText(slot, { en: value });
      setSavedAt(Date.now());
      onSaved?.(hotel.slug, value);
    } catch (e) {
      setError(e?.message || "Save failed");
    }
    setBusy(false);
  };

  const reset = () => {
    setDraft("");
    setError(null);
  };

  const preview = () => {
    if (isActiveHere) {
      stop();
      return;
    }
    playGuide({
      src: liveSrc,
      slug: hotel.slug,
      propertyName: hotel.name,
      guideTitle: "Audio guide (preview)",
    });
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    setUploadPct(0);
    // Light "fake" progress feedback while the upload is in flight —
    // fetch() doesn't expose real progress without XHR; the UI feels
    // responsive thanks to this ticker.
    const tick = setInterval(() => {
      setUploadPct((p) => (p < 88 ? p + 4 : p));
    }, 220);
    try {
      const out = await uploadAudio(slot, file);
      clearInterval(tick);
      setUploadPct(100);
      const absoluteUrl = out.url.startsWith("http") ? out.url : `${API}${out.url}`;
      setDraft(absoluteUrl);
      // Auto-save so the brand doesn't have to click twice after a successful upload.
      await save(absoluteUrl);
    } catch (e) {
      clearInterval(tick);
      setUploadPct(0);
      setError(e?.message || "Upload failed");
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  return (
    <li
      className="border border-[#2C2621]/15 bg-white p-4 md:p-5 flex flex-col gap-4"
      data-testid={`admin-audio-row-${hotel.slug}`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Hotel info */}
        <div className="flex items-start gap-3 md:w-72 shrink-0">
          <img
            src={hotel.image}
            alt={hotel.name}
            loading="lazy"
            className="w-16 h-12 object-cover shrink-0 border border-[#2C2621]/10"
          />
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#A07042]">{hotel.category || "Hotel"}</p>
            <p className="font-serif-x text-base text-[#2C2621] leading-tight truncate">{hotel.name}</p>
            {hotel.location ? (
              <p className="text-[11px] text-[#5C5248] mt-0.5 truncate">{pick(hotel.location, lang)}</p>
            ) : null}
          </div>
        </div>
        {/* URL field */}
        <div className="flex-1 min-w-0">
          <label
            htmlFor={`audio-url-${hotel.slug}`}
            className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] block mb-1"
          >
            Audio guide URL (MP3 / OGG / WAV)
          </label>
          <input
            id={`audio-url-${hotel.slug}`}
            type="url"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={DEFAULT_AUDIO_GUIDE_SRC}
            className="w-full bg-[#FDFBF7] border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-3 py-2 text-sm text-[#2C2621] font-mono"
            data-testid={`admin-audio-input-${hotel.slug}`}
          />
          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#5C5248]">
            {isUsingDefault ? (
              <span className="text-[#A07042]">Using default demo guide</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[#2C7C56]">
                <CheckCircle2 className="w-3 h-3" /> Custom URL set
              </span>
            )}
            {error ? (
              <span className="inline-flex items-center gap-1 text-[#A35133]">
                <AlertCircle className="w-3 h-3" /> {error}
              </span>
            ) : null}
            {savedAt ? (
              <span className="inline-flex items-center gap-1 text-[#2C7C56]">
                <CheckCircle2 className="w-3 h-3" /> Saved
              </span>
            ) : null}
          </div>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={preview}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] text-[11px] tracking-[0.18em] uppercase transition-colors"
            data-testid={`admin-audio-preview-${hotel.slug}`}
          >
            <Headphones className="w-3 h-3" />
            {isActiveHere ? "Stop" : "Preview"}
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#2C2621]/15 hover:border-[#A35133] text-[#5C5248] hover:text-[#A35133] text-[11px] tracking-[0.18em] uppercase transition-colors"
            title="Clear / reset to default"
            data-testid={`admin-audio-reset-${hotel.slug}`}
          >
            <RotateCcw className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => save()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-50"
            data-testid={`admin-audio-save-${hotel.slug}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
        </div>
      </div>

      {/* Upload drop-zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileRef.current?.click(); } }}
        role="button"
        tabIndex={0}
        aria-label={`Upload audio guide for ${hotel.name}`}
        className={`relative flex flex-col sm:flex-row items-center justify-center gap-3 px-4 py-3 border-2 border-dashed cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542] ${
          dragOver ? "border-[#C16542] bg-[#FDF8EE]" : "border-[#2C2621]/20 bg-[#FDFBF7] hover:border-[#C16542]"
        }`}
        data-testid={`admin-audio-dropzone-${hotel.slug}`}
      >
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT_AUDIO}
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="sr-only"
          data-testid={`admin-audio-fileinput-${hotel.slug}`}
        />
        <UploadCloud className={`w-5 h-5 ${dragOver ? "text-[#C16542]" : "text-[#5C5248]"}`} strokeWidth={1.6} />
        <p className="text-sm text-[#2C2621] text-center sm:text-left">
          {uploading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Uploading… {uploadPct}%
            </span>
          ) : (
            <>
              <span className="hidden sm:inline">Drag & drop an audio file here, or </span>
              <span className="underline decoration-[#D4A373] decoration-2 underline-offset-2">
                browse to upload
              </span>
              <span className="block sm:inline text-[11px] text-[#5C5248] sm:ml-2">
                MP3 · OGG · WAV · M4A · up to 32 MB. Upload auto-saves the URL.
              </span>
            </>
          )}
        </p>
        {uploading ? (
          <span
            aria-hidden="true"
            className="absolute left-0 bottom-0 h-[3px] bg-[#C16542] transition-[width] duration-200"
            style={{ width: `${uploadPct}%` }}
          />
        ) : null}
      </div>
    </li>
  );
};

const AdminAudioInner = () => {
  const { lang } = useLanguage();
  const [ready, setReady] = useState(false);
  const [urls, setUrls] = useState(() => new Map());
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureTextLoaded();
      if (cancelled) return;
      const next = new Map();
      for (const h of HOTELS) {
        const slot = slotForAudioGuide(h.slug);
        const v = getCachedText(slot);
        next.set(h.slug, (v?.en || v?.fr || v?.es || "").trim());
      }
      setUrls(next);
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const onSaved = (slug, url) => {
    setUrls((m) => {
      const c = new Map(m);
      c.set(slug, url);
      return c;
    });
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return HOTELS;
    const q = query.trim().toLowerCase();
    return HOTELS.filter((h) =>
      h.name.toLowerCase().includes(q) ||
      h.slug.toLowerCase().includes(q) ||
      (h.location && (pick(h.location, lang) || "").toLowerCase().includes(q))
    );
  }, [query, lang]);

  const setCount = useMemo(() => {
    let n = 0;
    urls.forEach((v) => { if ((v || "").trim()) n++; });
    return n;
  }, [urls]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#2C2621] pt-6 pb-32">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542] mb-6"
          data-testid="admin-audio-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back to admin
        </Link>
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="overline text-[#C16542]">CMS · Audio</p>
            <h1 className="font-serif-x text-3xl md:text-4xl mt-1">Audio guides</h1>
            <p className="text-sm text-[#5C5248] mt-2 max-w-prose">
              Set the narration URL for each property. Save it once and the persistent floating
              audio player will pick it up automatically the next time a guest taps the headphones icon
              on the corresponding hotel or bivouac card. Leave a row empty to fall back to the
              default demo guide.
            </p>
          </div>
          <div className="text-right text-[11px] text-[#5C5248] shrink-0">
            <p className="tabular-nums">{setCount} / {HOTELS.length} properties configured</p>
            <p className="mt-0.5">Hosted MP3 / OGG / WAV URLs recommended</p>
          </div>
        </header>
        <div className="relative mb-5">
          <Search className="w-4 h-4 text-[#5C5248]/60 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter properties…"
            className="w-full bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none pl-9 pr-3 py-2 text-sm text-[#2C2621]"
            data-testid="admin-audio-search"
          />
        </div>
        {!ready ? (
          <div className="text-sm text-[#5C5248] italic flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading saved URLs…
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#5C5248] italic">No properties match your search.</p>
        ) : (
          <ul className="space-y-3" data-testid="admin-audio-list">
            {filtered.map((h) => (
              <HotelAudioRow
                key={h.slug}
                hotel={h}
                lang={lang}
                initialUrl={urls.get(h.slug) || ""}
                onSaved={onSaved}
              />
            ))}
          </ul>
        )}
        <aside className="mt-10 p-5 border border-[#D4A373]/35 bg-[#FDF8EE] text-sm text-[#5C5248]">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#A07042] mb-1">Where it appears</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Homepage &ldquo;Our Collection&rdquo; property cards (headphones icon, next to the play-video button).</li>
            <li>Persistent floating audio player at the bottom of every page — keeps playing as the guest navigates.</li>
          </ul>
          <p className="text-[11px] mt-3 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Default fallback: <code className="font-mono text-[#2C2621]">{DEFAULT_AUDIO_GUIDE_SRC}</code>
          </p>
        </aside>
      </div>
    </div>
  );
};

const AdminAudioPage = () => (
  <AdminAuthGate>
    <AdminAudioInner />
  </AdminAuthGate>
);

export default AdminAudioPage;
